import React, { useEffect, useMemo, useState } from 'react';

import PageWrapper from '../PageWrapper';
import Page from '../Page';
import { RouteComponentProps } from 'react-router';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';

import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import { MenuBar } from '@go-client/components';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  BubbleAccordion,
  BubbleButton,
  BubbleDrawer,
  BubbleOverlay,
  BubbleBadge,
  BubbleImageThumbnail,
  BubbleInput,
  BubbleAlert,
  BubbleFormGroup,
  BubbleTypeahead,
} from '@go-client/components/BubbleUI';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import ReserveNavigation from '@go-client/components/Navigations/ReserveNavigation';
import {
  deleteScheduleByEvent,
  find as findReservables,
  findSchedules,
} from '@go-client/lib/api/reservable';
import { ReservableEventTemplate, RESERVABLE_EXPERIENCE_TYPE } from '@go-shared/types/reservable';
import { ReserveSchedule } from '@go-shared/types/reserveSchedule';
import ReservableSchedulePath, {
  ReservableScheduleFormMode,
} from '@go-client/components/ReservableSchedulePath/ReservableSchedulePath';
import { getUsers } from '@go-client/lib/api/user';
import { User } from '@go-shared/types/user';
import {
  canHostEvents,
  canManageAllSchedules,
  canManageAllReservations,
  canCreateExperiences,
} from '@go-shared/lib/permissions';
import qslib from '@go-shared/lib/querystring';
import ReserveSubNavigation from '@go-client/components/Navigations/ReserveSubNavigation';
import { mapBy, notFalsy, notUndefined, onlyUnique } from '@go-shared/lib/functional';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cherrypickImages } from '@go-client/lib/api/image';
import { Image } from '@go-shared/types/image';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { populate } from '@go-shared/lib/image';
import { makeFullName, safeIdCompare } from '@go-shared/lib/utils';
import Fuse from 'fuse.js';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { BareEventTemplate } from '@go-shared/types/eventTemplate';
import useThresholdClick from '@go-client/hooks/useThresholdClick';
import { BubbleTypeaheadValue } from '@go-client/components/BubbleUI/BubbleTypeahead';

interface ScheduleTableItem {
  id: string;
  eventTitle: string;
  description: string;
  hostCount: number;
  scheduleCount: number;
  ticketCount: number;
  packagesCount: number;
  addonsCount: number;
  type: RESERVABLE_EXPERIENCE_TYPE;
  _template: BareEventTemplate;
  _schedule?: ReserveSchedule;
}

interface ReservablesFilters {
  selectedHosts: Array<BubbleTypeaheadValue<User | null>>;
}

function makeDefaultFilters(user: User) {
  const selectedHosts: Array<BubbleTypeaheadValue<User>> = [];

  if (canHostEvents(user)) {
    selectedHosts.push({ key: user._id, item: user, refKey: user._id });
  }

  const filters: ReservablesFilters = {
    selectedHosts,
  };

  return filters;
}

interface ReservablesSchedulesParams {
  property: string;
}

type ReservablesSchedulesProps = RouteComponentProps<ReservablesSchedulesParams>;

const ReservablesSchedules: React.FC<ReservablesSchedulesProps> = (
  props: ReservablesSchedulesProps
) => {
  const { brand } = useServerInfo();

  const [loading, setLoading] = useState(true);
  const [loadingImages, setLoadingImages] = useState(false);
  const [mode, setMode] = useState<ReservableScheduleFormMode | undefined>(undefined);
  const { activeProperty } = useActiveProperty();
  const { user } = useUserInfo();
  const [filters, setFilters] = useState<ReservablesFilters>(makeDefaultFilters(user));

  const [dataBundle, setDataBundle] = useState<{
    schedules: ReserveSchedule[];
    reservables: ReservableEventTemplate[];
    users: User[];
  }>();
  const [images, setImages] = useState<Image[]>([]);

  const [search, setSearch] = useState('');
  const [showDelete, setShowDelete] = useState<{ [key: string]: boolean }>({});

  const allSchedules = dataBundle?.schedules ?? [];
  const reservables = dataBundle?.reservables ?? [];
  const users = dataBundle?.users ?? [];

  async function fetchData() {
    setLoading(true);
    try {
      const [schedules, reservables, users] = await Promise.all([
        findSchedules(activeProperty._id),
        findReservables(activeProperty._id),
        getUsers(activeProperty._id),
      ]);

      setDataBundle({
        schedules,
        reservables,
        users: users.filter(canHostEvents),
      });
    } finally {
      setLoading(false);
    }
  }

  async function fetchImages() {
    setLoadingImages(true);

    try {
      const images = await cherrypickImages(
        [
          ...allSchedules.map(s => s.eventTemplate.images).flat(),
          ...reservables.map(r => r.images).flat(),
        ]
          .filter(onlyUnique)
          .filter(notUndefined)
      );
      const populatedCherryPickImages = images.map(i => populate(i, brand.imageTypes));

      setImages(populatedCherryPickImages);
    } finally {
      setLoadingImages(false);
    }
  }

  useEffect(() => {
    fetchData().catch(console.error);
  }, [activeProperty]);

  useEffect(() => {
    fetchImages().catch(console.error);
  }, [dataBundle]);

  useEffect(() => {
    if (!dataBundle) return;
    const qs = qslib.readQs();
    if (typeof qs.experience !== 'string') return;
    const templateId = qs.experience;
    const template =
      dataBundle.schedules.find(s => s.eventTemplate._id === templateId)?.eventTemplate ??
      dataBundle.reservables.find(r => r._id === templateId);
    if (template) {
      setMode({ type: 'edit', eventTemplate: template });
    }
  }, [dataBundle]);

  const scheduledItems: ScheduleTableItem[] = allSchedules
    .filter(s => {
      if (filters.selectedHosts.length === 0) return true;
      return filters.selectedHosts.some(h => s.rules.some(r => r.host?._id === h.key));
    })
    .map(s => ({
      id: s._id,
      eventTitle: s.eventTemplate.title,
      description: s.eventTemplate.description,
      hostCount: s.rules
        .map(r => r.host)
        .filter(notFalsy)
        .filter(onlyUnique).length,
      scheduleCount: s.rules.filter(r => !r.hidden).length,
      ticketCount: s.eventTemplate.reservables?.tickets.length ?? 0,
      packagesCount: s.eventTemplate.reservables?.packages.length ?? 0,
      addonsCount: s.eventTemplate.reservables?.addons.length ?? 0,
      type: s.eventTemplate.reservables?.experienceType ?? 'ticket',
      _template: s.eventTemplate,
      _schedule: s,
    }));

  const unscheduledItems: ScheduleTableItem[] =
    filters.selectedHosts.length > 0
      ? []
      : reservables
          .filter(r => !allSchedules.some(s => safeIdCompare(s.eventTemplate._id, r._id)))
          .map(r => ({
            id: r._id,
            eventTitle: r.title,
            description: r.description,
            hostCount: 0,
            scheduleCount: 0,
            ticketCount: r.reservables.tickets.length ?? 0,
            packagesCount: r.reservables.packages.length ?? 0,
            addonsCount: r.reservables.addons.length ?? 0,
            type: r.reservables.experienceType,
            _template: r,
          }));

  const scheduledFuse = useMemo(() => {
    return new Fuse(scheduledItems, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['eventTitle'],
    });
  }, [scheduledItems]);

  const unscheduledFuse = useMemo(() => {
    return new Fuse(unscheduledItems, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['eventTitle'],
    });
  }, [unscheduledItems]);

  const scheduledFuseSearchResults = useMemo(() => {
    if (!search) return undefined;
    return scheduledFuse.search(search).map(s => s.item);
  }, [scheduledFuse, search]);

  const unscheduledFuseSearchResults = useMemo(() => {
    if (!search) return undefined;
    return unscheduledFuse.search(search).map(s => s.item);
  }, [unscheduledFuse, search]);

  const filteredScheduledItems = scheduledFuseSearchResults || scheduledItems;
  const filteredUnscheduledItems = unscheduledFuseSearchResults || unscheduledItems;

  const imagesMap = mapBy(images, i => i.key);

  function handleEditSchedule(eventTemplate: BareEventTemplate) {
    setMode({
      type: 'edit',
      eventTemplate,
    });
    qslib.writeQs({ experience: eventTemplate._id });
  }

  async function handleDeleteSchedule(templateId: string) {
    await deleteScheduleByEvent(activeProperty._id, templateId);
    await fetchData().catch(console.error);
  }

  function handleCloseDrawer() {
    setMode(undefined);
    qslib.writeQs({});
  }

  async function handleSubmitSchedule() {
    await fetchData().catch(console.error);
    handleCloseDrawer();
  }

  function rowForItem(item: ScheduleTableItem, index: number) {
    return (
      <tr
        key={index}
        className="bg-white shadow relative cursor-pointer"
        {...useThresholdClick({
          onClick: () => handleEditSchedule(item._template),
        })}
      >
        <td className="p-2 w-0">
          <BubbleImageThumbnail
            className="w-16"
            purpose="plain"
            loading={loadingImages}
            thumbnail={imagesMap[item._template.images[0]]}
          />
        </td>
        <td className="p-2 w-0">
          <FontAwesomeIcon
            fixedWidth
            className="text-navy text-xl mt-1"
            icon={item.type === 'ticket' ? 'ticket' : 'diamonds-4'}
          />
        </td>
        <td className="p-2">
          <div>
            <div className="text-lg font-semibold flex items-center text-navy">
              {item.eventTitle}
            </div>
            <div className="text-slate">{item.description}</div>
          </div>
        </td>
        <td className="p-2 whitespace-nowrap">
          <div className="flex items-center">
            <BubbleBadge
              className="mt-1 mr-1 whitespace-nowrap-i"
              variant={item.hostCount > 0 ? 'grape' : 'smoke'}
            >
              <FontAwesomeIcon fixedWidth className="mr-1" icon="user" />
              <span>{item.hostCount}</span>
            </BubbleBadge>
            <BubbleBadge
              className="mt-1 mr-1 whitespace-nowrap-i"
              variant={item.scheduleCount > 0 ? 'emerald' : 'smoke'}
            >
              <FontAwesomeIcon fixedWidth className="mr-1" icon="calendar-lines" />
              <span>{item.scheduleCount}</span>
            </BubbleBadge>
            <BubbleBadge
              className="mt-1 mr-1 whitespace-nowrap-i ml-4"
              variant={item.ticketCount > 0 ? 'lime' : 'smoke'}
            >
              <FontAwesomeIcon fixedWidth className="mr-1" icon="ticket" />
              <span>{item.ticketCount}</span>
            </BubbleBadge>
            <BubbleBadge
              className="mt-1 mr-1 whitespace-nowrap-i"
              variant={item.packagesCount > 0 ? 'banana' : 'smoke'}
            >
              <FontAwesomeIcon fixedWidth className="mr-1" icon="box-open" />
              <span>{item.packagesCount}</span>
            </BubbleBadge>
            <BubbleBadge
              className="mt-1 mr-1 whitespace-nowrap-i"
              variant={item.addonsCount > 0 ? 'ocean' : 'smoke'}
            >
              <FontAwesomeIcon fixedWidth className="mr-1" icon="puzzle-piece" />
              <span>{item.addonsCount}</span>
            </BubbleBadge>
          </div>
        </td>
        <td className={`p-2 w-0 whitespace-nowrap pr-4 ${!item._schedule ? 'text-right' : ''}`}>
          <BubbleButton
            onClick={() => handleEditSchedule(item._template)}
            className="p-2-i"
            variant="navy"
            inverse
            icon="edit"
          />
          {item._schedule && canManageAllSchedules(user) && (
            <>
              {!showDelete[item.id] && (
                <BubbleButton
                  className="p-2-i"
                  variant="crimson"
                  inverse
                  onClick={() => {
                    setShowDelete({ ...showDelete, [item.id]: true });
                  }}
                  icon="trash-alt"
                />
              )}
              {showDelete[item.id] && (
                <>
                  <BubbleButton
                    className="p-2-i"
                    variant="crimson"
                    inverse
                    onClick={() => {
                      item._schedule && handleDeleteSchedule(item._schedule.eventTemplate._id);
                    }}
                    icon="trash-check"
                  />
                  <BubbleButton
                    className="p-2-i"
                    variant="slate"
                    inverse
                    onClick={() => {
                      setShowDelete({ ...showDelete, [item.id]: false });
                    }}
                    icon="trash-xmark"
                  />
                </>
              )}
            </>
          )}
        </td>
      </tr>
    );
  }

  const hostOpts = users.map(u => ({
    key: u._id,
    item: u,
  }));

  return (
    <PageWrapper {...props}>
      <Page className="Reservables absolute flex items-stretch">
        <MenuBar {...props} property={activeProperty} />
        <div className="SideMenu bg-turquoise-darkest flex flex-col">
          <div className="bg-white flex flex-col items-center">
            <BrandLogo logo={activeProperty.brand.logo} />
            <PropertyBanner {...props} property={activeProperty} swappable />
          </div>
          <div className={`flex flex-col relative items-stretch p-4 bg-turquoise-darker`}>
            <div className="grid gap-2">
              {canCreateExperiences(user) && (
                <BubbleButton
                  variant="lime"
                  className="text-left"
                  icon="sparkles"
                  onClick={() => {
                    setMode({
                      type: 'create',
                    });
                  }}
                >
                  Create Experience
                </BubbleButton>
              )}
            </div>
          </div>
          <BubbleAccordion
            variant="turquoise"
            className="flex-grow flex flex-col border-t border-turquoise-darkest"
            labels={['Filters']}
            open={[0]}
            locked={[0]}
          >
            <div className="grid gap-4 py-4">
              {canManageAllReservations(user) && (
                <BubbleFormGroup label="Host" labelClassName="text-white-i" className="px-4">
                  <BubbleTypeahead
                    block
                    variant="turquoise"
                    placeholder="Filter hosts shown..."
                    menuClassName="max-h-80 overflow-y-auto"
                    value={filters.selectedHosts}
                    options={hostOpts}
                    labelExtractor={p => (p ? makeFullName(p.firstName, p.lastName) : 'No Host')}
                    onAdd={v =>
                      setFilters({
                        ...filters,
                        selectedHosts: [...filters.selectedHosts, v],
                      })
                    }
                    onRemove={v =>
                      setFilters({
                        ...filters,
                        selectedHosts: filters.selectedHosts.filter(v2 => v2.key !== v.key),
                      })
                    }
                    onClearAll={() => setFilters({ ...filters, selectedHosts: [] })}
                  />
                </BubbleFormGroup>
              )}
            </div>
          </BubbleAccordion>
        </div>
        <div className="ReservablesContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="ReservablesHeader align-with-logo flex items-end px-5 sticky top-0
              left-0 z-10 bg-blue-lightest"
          >
            <div className="mb-3 pl-3 flex-grow flex items-end">
              <ReserveNavigation active="reserve" />
            </div>
            <div
              className="absolute w-full bg-blue-lightest flex border-blue-lightest -mx-5 px-5"
              style={{
                height: 'calc(40px + 0.75rem)',
                bottom: 'calc(-40px - 0.75rem)',
                borderBottomWidth: '0.75rem',
                zIndex: 10,
              }}
            >
              <ReserveSubNavigation active="experiences" property={activeProperty} />
            </div>
          </div>
          <div className="ReservablesBody mx-5 content-minus-logo-minus-bar min-w-900">
            <div style={{ marginTop: 'calc(40px + 0.75rem)' }} className="pt-1">
              <div className="sticky z-10 bg-blue-lightest" style={{ top: 187 }}>
                <BubbleInput
                  type="text"
                  block
                  value={search}
                  onValue={setSearch}
                  placeholder="Search by experience..."
                />
              </div>
              <BubbleOverlay loading={loading} loadingMinHeight="medium" hideBg>
                <table className="w-full border-separate border-spacing-y-4">
                  <tbody>
                    {filteredScheduledItems.length > 0 && (
                      <>
                        <tr>
                          <td></td>
                          <td></td>
                          <td className="px-2 text-left">
                            <div className="font-bold uppercase text-slate">
                              Experiences With Schedules
                            </div>
                            <div className="text-smoke text-sm mt-1 italic">
                              Use the trash icon to delete all schedules from the experience.
                            </div>
                          </td>
                          <td className="px-2 text-left font-bold uppercase text-slate align-bottom">
                            Summary
                          </td>
                          <td></td>
                        </tr>
                        {filteredScheduledItems.map((item, index) => rowForItem(item, index))}
                      </>
                    )}
                    {filteredUnscheduledItems.length > 0 && (
                      <>
                        <tr>
                          <td></td>
                          <td></td>
                          <td className="px-2 text-left">
                            <div className="font-bold uppercase text-slate">
                              Experiences Without Schedules
                            </div>
                            <div className="text-smoke text-sm mt-1 italic">
                              To remove these experiences from this view, head to the Event Library
                              and delete the related ticket or rental.
                            </div>
                          </td>
                          <td className="px-2 text-left font-bold uppercase text-slate align-bottom">
                            Summary
                          </td>
                          <td></td>
                        </tr>
                        {filteredUnscheduledItems.map((item, index) => rowForItem(item, index))}
                      </>
                    )}
                  </tbody>
                </table>
                {!loading &&
                  filteredScheduledItems.length === 0 &&
                  filteredUnscheduledItems.length === 0 && (
                    <BubbleAlert variant="banana" className="mt-3">
                      No experiences found. Try adjusting your search or filters.
                    </BubbleAlert>
                  )}
              </BubbleOverlay>
            </div>
            <div className="mt-3">&nbsp;</div>
          </div>
        </div>
        {mode && (
          <BubbleDrawer containerClassName="bg-eggshell" onClose={handleCloseDrawer}>
            <ReservableSchedulePath
              mode={mode}
              users={users}
              onClose={handleCloseDrawer}
              onSubmitSchedule={handleSubmitSchedule}
              onSave={fetchData}
            />
          </BubbleDrawer>
        )}
      </Page>
    </PageWrapper>
  );
};

export default ReservablesSchedules;
